import * as React from "react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
} from "./sheet"
import { Button } from "./button"
import { X } from "lucide-react"
import { ScrollArea } from "./scroll-area"

/**
 * SlideDrawer - A reusable right-side drawer component for Add/Edit/View operations
 * 
 * @param {boolean} open - Controls drawer visibility
 * @param {function} onOpenChange - Callback when drawer open state changes
 * @param {string} title - Drawer header title
 * @param {string} description - Optional description below title
 * @param {string} size - Drawer width: 'sm' | 'default' | 'lg' | 'xl' | 'full'
 * @param {React.ReactNode} children - Drawer content
 * @param {React.ReactNode} footer - Optional footer content (buttons, etc.)
 * @param {boolean} showClose - Show X close button (default: true)
 */
export const SlideDrawer = ({
  open,
  onOpenChange,
  title,
  titleExtra,
  description,
  size = "lg",
  children,
  footer,
  showClose = true,
  dismissable = false,
  className = "",
}) => {
  // In non-dismissable mode (Add/Edit) the sheet can only close via the X /
  // Cancel — prevents losing unsaved form input. Read-only View sheets pass
  // dismissable so overlay-click + Escape close naturally.
  const handleInteractOutside = (e) => {
    if (!dismissable) e.preventDefault()
  }
  const handleEscape = (e) => {
    if (!dismissable) e.preventDefault()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        size={size} 
        className={`flex flex-col h-full p-0 sheet-ground ${className}`}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscape}
        hideCloseButton={true}
      >
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <SheetTitle className="text-[13px] font-extrabold uppercase tracking-[0.09em]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {title}
              </SheetTitle>
              {titleExtra}
              {description && (
                <SheetDescription className="mt-1">
                  {description}
                </SheetDescription>
              )}
            </div>
            {showClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="h-8 w-8"
                data-testid="drawer-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Scrollable Content Area */}
        <ScrollArea className="flex-1 px-6 py-4">
          {children}
        </ScrollArea>

        {/* Footer */}
        {footer && (
          <SheetFooter className="px-6 py-4 border-t border-border flex-shrink-0">
            {footer}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default SlideDrawer
